import { log } from './log';
import { addStyle } from '../utils/addStyle';
import { PlaylistItem } from './types';

type PixelsSetupParam = {
  pageId: string;
};

const forceCannedMatchOverride = (prefix = '') => {
  const params = new URLSearchParams(window?.location?.search);
  const key = `${prefix}has_match`;
  return params.has(key);
};

let pixelsAdTag: string;
let publisherId: string;
let playlistSlug = 'sport';
let playlistEnabled = true;
let pixelsPageId: string;
let pixelsPlaylist: PlaylistItem[];
let isProd = true;
const ENABLED_ON_PROD = true;
const isProdCheck = (domain: string) => /pub.pixels.ai/.test(domain);
const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

const pixelsMatchBaseUrl = __MATCH_VIDEO_ENDPOINT__;
const pixelsPubBaseUrl = __PUB_ENDPOINT__;

const playerDivId = 'pixels_inject_video_player_inner';
let pixelsInjectVideoPlayerOpacity = 0;
let pixelsInjectVideoPlayer: HTMLDivElement;

const getPlaylist = async (pixelsPageId: string): Promise<PlaylistItem[]> => {
  if (forceCannedMatchOverride()) {
    log('pixels', 'injecting test match...');
    return [
      {
        media_id: 'Z7Hcu01G85qsWjth4wNdbwkH55kjiGPbvyZBpNI01mYBE',
        contextual: true,
      } as PlaylistItem,
    ]; // sample clock countdown
  } else if (forceCannedMatchOverride('caps_')) {
    log('pixels', 'injecting caps test match...');
    return [
      {
        media_id: 'By57029eMCNUwxJDsrxckO6yYVHcMom6LG6R6EuNp02es',
        contextual: true,
      } as PlaylistItem,
    ]; // sample with captions
  }
  const endpoint = new URL(pixelsMatchBaseUrl);
  endpoint.pathname =
    endpoint.pathname.replace(/\/$/, '') + `/horizontal/${publisherId}/${playlistSlug}/${pixelsPageId}`;
  log('pixels', 'endpoint', endpoint.toString());

  try {
    return await (await fetch(endpoint.toString())).json();
  } catch (_) {
    log('pixels', 'inject match NOT found');
    return [];
  }
};

// init player with media id
const pixelsInjectCallback = (publisherId: string) => async () => {
  if (pixelsPlaylist) {
    const { pixels } = window;
    pixels(playerDivId).setup({
      pageId: pixelsPageId,
      playlist: pixelsPlaylist,
      isInjected: true,
      adTagUrl: pixelsAdTag,
      publisherId,
      preferredCCLang: publisherId === 'kooora' ? 'ar' : null,
    });
  }
};

// add player script
const pixelsInjectLoadPlayer = () => {
  const pixelsInjectScript = document.createElement('script');
  pixelsInjectScript.defer = true;
  pixelsInjectScript.onload = pixelsInjectCallback(publisherId);
  let scriptSrc;
  scriptSrc = `${pixelsPubBaseUrl}/pixels-player-lib.js?id=${publisherId}`;
  pixelsInjectScript.src = scriptSrc;
  document.head.appendChild(pixelsInjectScript);
};

// fade up black rect
const pixelsInjectFadeUp = () => {
  if (pixelsInjectVideoPlayerOpacity < 1) {
    pixelsInjectVideoPlayerOpacity += 0.1;
    setTimeout(() => pixelsInjectFadeUp(), 25);
  } else {
    pixelsInjectLoadPlayer();
  }
  pixelsInjectVideoPlayer.style.opacity = '' + pixelsInjectVideoPlayerOpacity;
};

const injectVideo = (pixelsInjectTag: string) => {
  // get hero image on page
  const pixelsInjectHeroImage = document.querySelector(pixelsInjectTag) as HTMLElement;
  pixelsInjectHeroImage.style.position = 'relative';
  const pixelsInjectHeroImageRect = pixelsInjectHeroImage.getBoundingClientRect();

  const rootClassSelector = 'pixels-root';

  // add invisible black rect over hero image
  pixelsInjectVideoPlayer = document.createElement('div');
  pixelsInjectVideoPlayer.style.backgroundColor = 'black';
  pixelsInjectVideoPlayer.style.position = 'absolute';
  pixelsInjectVideoPlayer.style.width = '100%';
  pixelsInjectVideoPlayer.style.height = '100%';
  pixelsInjectVideoPlayer.style.top = '0';
  pixelsInjectVideoPlayer.style.opacity = '0';
  pixelsInjectVideoPlayer.classList.add(rootClassSelector);

  pixelsInjectHeroImage.appendChild(pixelsInjectVideoPlayer);

  // add div for player holder
  // set marginTop to percentage to account for resizing...
  const marginTop =
    ((pixelsInjectHeroImageRect.height - pixelsInjectHeroImageRect.width * 0.5625) /
      2 /
      pixelsInjectHeroImageRect.width) *
    100;
  const pixelsInjectVideoPlayerHolder = document.createElement('div');
  pixelsInjectVideoPlayerHolder.style.marginTop = marginTop + '%';
  pixelsInjectVideoPlayer.appendChild(pixelsInjectVideoPlayerHolder);

  // add div for player inner
  const pixelsInjectVideoPlayerInner = document.createElement('div');
  pixelsInjectVideoPlayerInner.id = playerDivId;
  pixelsInjectVideoPlayerHolder.appendChild(pixelsInjectVideoPlayerInner);

  // start fade up
  pixelsInjectFadeUp();

  addStyle(`
    .${rootClassSelector} {
      color: #fff;
      background-color: #000;
    }
    .${rootClassSelector} .vjs-control-bar * {
      color: #fff !important;
    }
  `);
};

const extractContextualVideoFromPlaylist = (playlist: any[]): any[] => {
  if (playlist[0]?.contextual) {
    return [playlist[0]];
  }
  return [];
};

const withinDeliveryCap = (deliveryCap: number) => {
  if (!deliveryCap) {
    return true;
  }
  const randomNumber = Math.random();
  return randomNumber <= deliveryCap;
};

const hasContextualVideo = (playlist: any[]): boolean => {
  return playlist[0]?.contextual;
};

export const pixelsInject = (elementId: string) => {
  return {
    setup: async (options: PixelsSetupParam, callback: (x: boolean) => void = null, deliveryCap: number = null) => {
      pixelsPageId = options?.pageId;
      // only inject video in test envs for now...
      if (ENABLED_ON_PROD || !isProd) {
        if (pixelsPageId) {
          pixelsPlaylist = await getPlaylist(pixelsPageId);
          if (!playlistEnabled) {
            pixelsPlaylist = extractContextualVideoFromPlaylist(pixelsPlaylist);
          }
          // only inject if we have a video...
          if (pixelsPlaylist.length > 0 && hasContextualVideo(pixelsPlaylist)) {
            if (!withinDeliveryCap(deliveryCap)) {
              // circuit breaker...
              if (callback) {
                callback(false);
              }
              return;
            }
            // allow some time for host page to settle before querying hero image rect...
            log('pixels', 'inject called with pixelsPageId and pixelsPlaylist', pixelsPageId, pixelsPlaylist);
            await sleep(250);
            injectVideo(elementId);
          }
        }
      } else {
        log('pixels', 'not running on prod...');
      }
      if (callback) {
        callback(Array.isArray(pixelsPlaylist) && pixelsPlaylist.length > 0 && hasContextualVideo(pixelsPlaylist));
      }
    },
    setAdTag: (adTag: string) => {
      pixelsAdTag = adTag;
    },
    setPubId: (pubId: string) => {
      if (pubId) {
        publisherId = pubId;
        log('pixels', 'inject', 'publisherId', publisherId);
      }
    },
    setPlaylist: (playlist: string) => {
      if (playlist) {
        playlistSlug = playlist;
        log('pixels', 'inject', 'playlistSlug', playlistSlug);
      }
    },
    disablePlaylist: () => {
      playlistEnabled = false;
      log('pixels', 'inject', 'playlist disabled');
    },
  };
};

try {
  // this won't work on IE 11 and below but I don't think it matters
  // because we don't support them?
  let currentSrc = document.currentScript?.getAttribute('src');
  if (!currentSrc.startsWith('http')) {
    currentSrc = window.location.origin + currentSrc;
  }
  isProd = isProdCheck(currentSrc);
  const assetUrl = new URL(currentSrc);
  const pubId = assetUrl.searchParams.get('id');
  if (pubId) {
    pixelsInject('').setPubId(pubId);
  }
} catch (exception) {
  console.error(exception);
}
